/**
 * FormIO Authentication and Header Management
 * 
 * Handles essential headers for FormIO requests including:
 * - Language header for internationalization
 * - CSRF protection
 * - Content-Type negotiation
 * - Modern FormIO header format compatibility
 * 
 * Note: Token-based authentication is commented out pending removal verification
 */

import { Formio } from '@formio/js';

/**
 * Configure FormIO with essential authentication and headers
 * Uses modern FormIO practices for the latest package versions
 */
export function configureFormIOAuth() {
    if (typeof Formio === 'undefined') {
        console.warn('[FormIO Auth] FormIO library not available');
        return;
    }

    // Store original request method
    const origRequest = Formio.request;
    
    // Track all FormIO component changes for debugging
    if (typeof window !== 'undefined') {
        window.addEventListener('formio.componentChange', (event) => {
            console.log('[FormIO DEBUG] Component Changed:', {
                component: event.detail?.component?.key || 'unknown',
                componentType: event.detail?.component?.type || 'unknown',
                componentLabel: event.detail?.component?.label || 'unknown',
                value: event.detail?.value,
                changed: event.detail?.changed,
                isSelect: event.detail?.component?.type === 'select',
                dataSrc: event.detail?.component?.dataSrc,
                dataUrl: event.detail?.component?.data?.url,
                fullComponent: event.detail?.component,
                timestamp: new Date().toISOString()
            });
        });
        
        console.log('[FormIO DEBUG] Component change listener installed');
    }
    
    // Override FormIO request to ensure proper headers
    Formio.request = function(url, method, data, header, options) {
        // FormIO expects headers in array format: [{name: 'key', value: 'val'}]
        // Handle both incoming formats and ensure proper output format
        
        let formattedHeader = header;
        
        // Convert to array format if not already
        if (!Array.isArray(formattedHeader)) {
            formattedHeader = [];
            
            // Convert object-style headers to FormIO array format
            if (header && typeof header === 'object') {
                for (const [key, value] of Object.entries(header)) {
                    formattedHeader.push({ name: key, value: value });
                }
            }
        }
        
        // Helper function to check if header already exists
        const hasHeader = (name) => formattedHeader.some(h => h.name === name);
        
        // Helper function to add header if not exists
        const addHeader = (name, value) => {
            if (!hasHeader(name) && value) {
                formattedHeader.push({ name, value });
            }
        };
        
        // === COMMENTED OUT: Token-based authentication pending removal ===
        // TODO: Remove after confirming no usage in codebase
        // const token = localStorage.getItem('token');
        // if (token) {
        //     addHeader('Authorization', `Bearer ${token}`);
        // }
        
        // === ESSENTIAL HEADERS FOR FORMIO ===
        
        // Language header for internationalization
        const lang = localStorage.getItem('lang') || 'en';
        addHeader('lang', lang);
        
        // Accept header for proper content negotiation
        addHeader('Accept', 'application/json');
        
        // Content-Type for requests with data
        if (data && method) {
            const httpMethod = method.toUpperCase();
            if (['POST', 'PUT', 'PATCH'].includes(httpMethod)) {
                addHeader('Content-Type', 'application/json');
            }
        }
        
        // CSRF token from meta tag (important for security)
        const csrfToken = document.querySelector('meta[name="csrf-token"]');
        if (csrfToken) {
            addHeader('X-CSRF-TOKEN', csrfToken.getAttribute('content'));
        }
        
        // Additional security headers that might be expected by backend
        addHeader('X-Requested-With', 'XMLHttpRequest');
        
        // Enhanced logging for debugging select component requests
        const isTicketRequest = url && url.includes('/Ticket/');
        const isSelectRequest = url && url.includes('/select/');
        const shouldLogDetailed = isTicketRequest || isSelectRequest;
        
        if (shouldLogDetailed) {
            console.log('[FormIO DEBUG] 🔍 SELECT/DETAIL REQUEST DETECTED:', {
                url: url,
                method: method || 'GET',
                isTicketDetailRequest: isTicketRequest,
                isSelectDropdownRequest: isSelectRequest,
                includeParams: url.includes('include=') ? url.split('include=')[1]?.split('&')[0] : 'none',
                headers: formattedHeader.map(h => `${h.name}: ${h.value}`),
                timestamp: new Date().toISOString(),
                stackTrace: new Error().stack
            });
        } else {
            console.log('[FormIO Auth] Request headers configured:', {
                url: url,
                method: method || 'GET',
                headerFormat: 'array',
                headerCount: formattedHeader.length,
                headers: formattedHeader.map(h => `${h.name}: ${h.value}`)
            });
        }
        
        // Call original request with properly formatted headers
        return origRequest.call(this, url, method, data, formattedHeader, {
            ...options,
            credentials: 'include', // Important for cookie-based session management
            withCredentials: true
        });
    };
    
    console.log('[FormIO Auth] Authentication headers configured successfully');
}

export default configureFormIOAuth;